import createSelector from "../../utils/reselect";

const navigationStateData = (state) => {
  const { navigation } = state;
  return navigation || {};
};

// Get the complete breadcrumb history
export const getBreadcrumbHistory = createSelector(
  navigationStateData,
  (navigation) => {
    const breadcrumbHistory = navigation.get("breadcrumbHistory");
    return breadcrumbHistory ? breadcrumbHistory.toJS() : [];
  }
);

// Get the current breadcrumb index
export const getCurrentBreadcrumbIndex = createSelector(
  navigationStateData,
  (navigation) => {
    return navigation.get("currentBreadcrumbIndex") ?? -1;
  }
);

// Get the current breadcrumb path (derived from history and current index)
export const getBreadcrumbPath = createSelector(
  [getBreadcrumbHistory, getCurrentBreadcrumbIndex],
  (breadcrumbHistory, currentIndex) => {
    if (currentIndex === -1 || !breadcrumbHistory.length) return [];
    return breadcrumbHistory.slice(0, currentIndex + 1);
  }
);

// Get the current entity
export const getCurrentEntity = createSelector(
  navigationStateData,
  (navigation) => {
    const currentEntity = navigation.get("currentEntity");
    return currentEntity ? currentEntity : null;
  }
);

// Get all entity contexts
export const getEntityContexts = createSelector(
  navigationStateData,
  (navigation) => {
    const entityContexts = navigation.get("entityContexts");
    return entityContexts ? entityContexts.toJS() : {};
  }
);

// Get navigation error
export const getNavigationError = createSelector(
  navigationStateData,
  (navigation) => navigation.get("error") || ""
);

// Get navigation loading state
export const getNavigationLoading = createSelector(
  navigationStateData,
  (navigation) => navigation.get("isNavigating") || false
);

// Get entity context for a specific entity
export const getEntityContext = (entityType, entityId) =>
  createSelector(getEntityContexts, (entityContexts) => {
    const entityKey = `${entityType}-${entityId}`;
    return entityContexts[entityKey];
  });

// Get selected tab for a specific entity
export const getEntitySelectedTab = (entityType, entityId) =>
  createSelector(getEntityContext(entityType, entityId), (context) => {
    return context?.selectedTab;
  });

// Get the breadcrumb path as formatted items for UI
export const getBreadcrumbItems = createSelector(
  getBreadcrumbPath,
  (breadcrumbPath) => {
    // Filter out dashboard items from breadcrumb display
    const filteredPath = breadcrumbPath.filter(
      (item) => item.type !== "dashboard"
    );

    return filteredPath.map((item, index) => ({
      ...item,
      isActive: index === filteredPath.length - 1,
      isClickable: index < filteredPath.length - 1,
    }));
  }
);

// Check if we can navigate back from current position
export const canNavigateBack = createSelector(
  getBreadcrumbPath,
  (breadcrumbPath) => {
    // Filter out dashboard items from breadcrumb, same as getBreadcrumbItems
    const filteredPath = breadcrumbPath.filter(
      (item) => item.type !== "dashboard"
    );
    // Can only go back if there are more than 1 non-dashboard items
    return filteredPath.length > 1;
  }
);

// Check if we can navigate forward in history
export const canNavigateForward = createSelector(
  [getBreadcrumbHistory, getCurrentBreadcrumbIndex],
  (breadcrumbHistory, currentIndex) => {
    return currentIndex < breadcrumbHistory.length - 1;
  }
);

// Get the parent entity of the current entity
export const getParentEntity = createSelector(
  getBreadcrumbPath,
  (breadcrumbPath) => {
    if (breadcrumbPath.length > 1) {
      return breadcrumbPath[breadcrumbPath.length - 2];
    }
    return null;
  }
);

// Get dashboard entity
export const getDashboardEntity = createSelector(
  getBreadcrumbPath,
  (breadcrumbPath) => {
    const dashboard = breadcrumbPath.find((item) => item.type === "dashboard");
    return (
      dashboard || { type: "dashboard", id: "dashboard", name: "Dashboard" }
    );
  }
);

// Check if current navigation is at dashboard
export const isAtDashboard = createSelector(
  getCurrentEntity,
  (currentEntity) => {
    return currentEntity && currentEntity.type === "dashboard";
  }
);

// Get navigation history for a specific entity type
export const getEntityHistory = (entityType) =>
  createSelector(getBreadcrumbHistory, (breadcrumbHistory) => {
    return breadcrumbHistory.filter((item) => item.type === entityType);
  });

// Get the last visited entity of a specific type
export const getLastVisitedEntity = (entityType) =>
  createSelector(getEntityHistory(entityType), (entityHistory) => {
    return entityHistory.length > 0
      ? entityHistory[entityHistory.length - 1]
      : null;
  });

// Check if a specific entity is in the breadcrumb path
export const isEntityInBreadcrumb = (entityType, entityId) =>
  createSelector(getBreadcrumbPath, (breadcrumbPath) => {
    return breadcrumbPath.some(
      (item) => item.type === entityType && item.id === entityId
    );
  });

// Check if a specific entity is in the breadcrumb history
export const isEntityInHistory = (entityType, entityId) =>
  createSelector(getBreadcrumbHistory, (breadcrumbHistory) => {
    return breadcrumbHistory.some(
      (item) => item.type === entityType && item.id === entityId
    );
  });

// Get the position of an entity in the breadcrumb path
export const getEntityPosition = (entityType, entityId) =>
  createSelector(getBreadcrumbPath, (breadcrumbPath) => {
    return breadcrumbPath.findIndex(
      (item) => item.type === entityType && item.id === entityId
    );
  });

// Get the position of an entity in the breadcrumb history
export const getEntityHistoryPosition = (entityType, entityId) =>
  createSelector(getBreadcrumbHistory, (breadcrumbHistory) => {
    return breadcrumbHistory.findIndex(
      (item) => item.type === entityType && item.id === entityId
    );
  });

// Get breadcrumb path up to a specific entity
export const getBreadcrumbPathUpTo = (entityType, entityId) =>
  createSelector(getBreadcrumbPath, (breadcrumbPath) => {
    const entityIndex = breadcrumbPath.findIndex(
      (item) => item.type === entityType && item.id === entityId
    );
    return entityIndex !== -1 ? breadcrumbPath.slice(0, entityIndex + 1) : [];
  });

// Get entities after a specific entity in breadcrumb
export const getEntitiesAfter = (entityType, entityId) =>
  createSelector(getBreadcrumbPath, (breadcrumbPath) => {
    const entityIndex = breadcrumbPath.findIndex(
      (item) => item.type === entityType && item.id === entityId
    );
    return entityIndex !== -1 ? breadcrumbPath.slice(entityIndex + 1) : [];
  });

// Get forward navigation options (entities that can be navigated to)
export const getForwardNavigationOptions = createSelector(
  [getBreadcrumbHistory, getCurrentBreadcrumbIndex],
  (breadcrumbHistory, currentIndex) => {
    if (currentIndex >= breadcrumbHistory.length - 1) return [];
    return breadcrumbHistory.slice(currentIndex + 1);
  }
);

// Get the next entity in history (for forward navigation)
export const getNextEntity = createSelector(
  [getBreadcrumbHistory, getCurrentBreadcrumbIndex],
  (breadcrumbHistory, currentIndex) => {
    if (currentIndex >= breadcrumbHistory.length - 1) return null;
    return breadcrumbHistory[currentIndex + 1];
  }
);

// Get the previous entity in history (for back navigation)
export const getPreviousEntity = createSelector(
  [getBreadcrumbHistory, getCurrentBreadcrumbIndex],
  (breadcrumbHistory, currentIndex) => {
    if (currentIndex <= 0) return null;
    return breadcrumbHistory[currentIndex - 1];
  }
);

// Get navigation state summary for debugging
export const getNavigationSummary = createSelector(
  [getBreadcrumbPath, getBreadcrumbHistory, getCurrentBreadcrumbIndex, getCurrentEntity, getEntityContexts],
  (breadcrumbPath, breadcrumbHistory, currentIndex, currentEntity, entityContexts) => {
    // Filter out dashboard items for consistent canGoBack logic
    const filteredPath = breadcrumbPath.filter(
      (item) => item.type !== "dashboard"
    );
    
    return {
      breadcrumbPath,
      breadcrumbHistory,
      currentBreadcrumbIndex: currentIndex,
      currentEntity,
      entityContexts,
      breadcrumbLength: breadcrumbPath.length,
      historyLength: breadcrumbHistory.length,
      canGoBack: filteredPath.length > 1,
      canGoForward: currentIndex < breadcrumbHistory.length - 1,
      isAtDashboard: currentEntity && currentEntity.type === "dashboard",
    };
  }
);

// Export all selectors as a single object for easier importing
export const navigationSelectors = {
  getBreadcrumbHistory,
  getCurrentBreadcrumbIndex,
  getBreadcrumbPath,
  getCurrentEntity,
  getEntityContexts,
  getNavigationError,
  getNavigationLoading,
  getEntityContext,
  getEntitySelectedTab,
  getBreadcrumbItems,
  canNavigateBack,
  canNavigateForward,
  getParentEntity,
  getDashboardEntity,
  isAtDashboard,
  getEntityHistory,
  getLastVisitedEntity,
  isEntityInBreadcrumb,
  isEntityInHistory,
  getEntityPosition,
  getEntityHistoryPosition,
  getBreadcrumbPathUpTo,
  getEntitiesAfter,
  getForwardNavigationOptions,
  getNextEntity,
  getPreviousEntity,
  getNavigationSummary,
};
